import { Bar, BarChart, CartesianGrid, LabelList, XAxis, YAxis } from 'recharts';
import type { TooltipPayloadEntry, TooltipValueType } from 'recharts';
import { ChartContainer, ChartTooltip, type ChartConfig } from '@/components/ui/chart';
import { t } from '@/i18n';
import type { PayrollTypeTotal } from '@ip/shared-types';
import { ChartCard } from './ChartCard';
import { formatAxis, formatPLN } from './chart-utils';

/**
 * Employer cost per employment type (UOP / zlecenie / powołanie / dzieło…).
 *
 * Employment types are NOMINAL — swapping their order changes nothing — so
 * every bar takes the SAME categorical slot (slot 1). Coloring each bar by its
 * own hue, or shading them darker-where-bigger, would spend the identity
 * channel re-encoding what the bar length already shows.
 *
 * Headcount is a second measure on a different scale: it goes in the tooltip
 * and the row labels, never on a second y-axis.
 *
 * Invariant: sum(byType.employerCost) === kpis.employerCostYtd (b2b excluded —
 * contractors are invoiced, not paid through payroll).
 */
interface Row {
  type: string;
  label: string;
  headcount: number;
  gross: number;
  employerCost: number;
}

function ByTypeTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: TooltipPayloadEntry<TooltipValueType, number | string>[];
}) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload as Row | undefined;
  if (!row) return null;

  return (
    <div className="border-border/50 bg-background grid min-w-[13rem] items-start gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs shadow-xl">
      <div className="font-medium">{row.label}</div>
      <div className="grid gap-1.5">
        <div className="flex w-full items-center gap-2">
          <div
            className="size-2.5 shrink-0 rounded-[2px]"
            style={{ background: 'var(--chart-1)' }}
          />
          <span className="text-muted-foreground">{t('dashboard.people.employerCostTotal')}</span>
          <span className="text-foreground ml-auto pl-4 font-mono font-medium tabular-nums">
            {formatPLN(row.employerCost)} PLN
          </span>
        </div>
        <div className="flex w-full items-center gap-2">
          <div className="size-2.5 shrink-0" aria-hidden="true" />
          <span className="text-muted-foreground">{t('dashboard.people.gross')}</span>
          <span className="text-foreground ml-auto pl-4 font-mono font-medium tabular-nums">
            {formatPLN(row.gross)} PLN
          </span>
        </div>
        <div className="flex w-full items-center gap-2">
          <div className="size-2.5 shrink-0" aria-hidden="true" />
          <span className="text-muted-foreground">{t('dashboard.people.headcount')}</span>
          <span className="text-foreground ml-auto pl-4 font-mono font-medium tabular-nums">
            {row.headcount}
          </span>
        </div>
      </div>
    </div>
  );
}

export function PayrollByTypeChart({ byType }: { byType: PayrollTypeTotal[] }) {
  const rows: Row[] = [...byType]
    .sort((a, b) => b.employerCost - a.employerCost)
    .map((row) => ({
      type: row.employmentType,
      label: t(`employees.type.${row.employmentType}`),
      headcount: row.headcount,
      gross: row.gross,
      employerCost: row.employerCost,
    }));

  // Single series → no legend box; the card title names what is plotted.
  const config: ChartConfig = {
    employerCost: { label: t('dashboard.people.employerCostTotal'), color: 'var(--chart-1)' },
  };

  if (rows.length === 0) {
    return (
      <ChartCard title={t('dashboard.people.byTypeTitle')}>
        <p className="text-muted-foreground py-12 text-center text-sm">
          {t('dashboard.people.noPayroll')}
        </p>
      </ChartCard>
    );
  }

  return (
    <ChartCard title={t('dashboard.people.byTypeTitle')}>
      <p className="text-muted-foreground mb-3 text-xs">{t('dashboard.people.byTypeHint')}</p>
      <ChartContainer
        config={config}
        className="w-full"
        style={{ height: `${Math.max(rows.length * 48 + 40, 140)}px` }}
      >
        <BarChart data={rows} layout="vertical" margin={{ right: 88, left: 4 }} barSize={20}>
          <CartesianGrid horizontal={false} strokeOpacity={0.4} />
          <XAxis type="number" tickFormatter={formatAxis} tickLine={false} axisLine={false} />
          <YAxis
            type="category"
            dataKey="label"
            tickLine={false}
            axisLine={false}
            width={148}
            tick={{ fontSize: 12 }}
          />
          <ChartTooltip content={<ByTypeTooltip />} />
          <Bar dataKey="employerCost" fill="var(--chart-1)" radius={[0, 4, 4, 0]}>
            <LabelList
              dataKey="employerCost"
              position="right"
              offset={10}
              className="fill-foreground font-medium tabular-nums"
              fontSize={12}
              formatter={(value) => `${formatAxis(Number(value))} PLN`}
            />
          </Bar>
        </BarChart>
      </ChartContainer>
      {/* Table view — the WCAG-clean twin: headcount is not encoded in the chart. */}
      <ul className="mt-4 flex flex-col gap-1.5 text-xs">
        {rows.map((row) => (
          <li key={row.type} className="flex items-baseline justify-between gap-3">
            <span className="text-muted-foreground truncate">{row.label}</span>
            <span className="shrink-0 tabular-nums">
              {t('dashboard.people.headcountValue', { count: row.headcount })}
            </span>
          </li>
        ))}
      </ul>
    </ChartCard>
  );
}
